import type { EffectPlugin } from '@shared/lib/effect-registry'
import type { OverlayEvent } from '@shared/types'
import type {
  PluginReadyMessage,
  PluginRejectedMessage,
  RegisterPluginMessage,
  RemoteSoundConfig,
} from '@shared/types/ws'

import { sessionService } from '@entities/session/sessionService'
import { usePluginI18nStore } from '@features/plugin-i18n'
import { audioService } from '@shared/lib/audio'
import { effectRegistry } from '@shared/lib/effect-registry'

import { pluginVerifier } from './PluginVerifier'

export type PluginStatusCallback = (msg: PluginReadyMessage | PluginRejectedMessage) => void

/**
 * Загружает и регистрирует плагин с бэкенда.
 * После завершения вызывает onStatus — бэкенд получит plugin_ready или plugin_rejected.
 * Бэкенд использует plugin_ready как сигнал что можно начинать слать события этого типа.
 */
export class DynamicPluginLoader {
  private blobUrls = new Map<string, string>()
  private onStatus: PluginStatusCallback = () => void 0

  setStatusCallback(cb: PluginStatusCallback): void {
    this.onStatus = cb
  }

  async load(message: RegisterPluginMessage): Promise<void> {
    const { pluginType, version, moduleCode, moduleCodeHash, signature, keyId } = message
    const incomingVersion = version ?? '0.0.0'

    // Словарь переводов кладём в i18n-store сразу: он runtime-проп и не влияет
    // на подпись/верификацию. Даже если это повторный register — обновим язык.
    if (message.i18n) {
      usePluginI18nStore
        .getState()
        .setDict(pluginType, message.locale ?? 'en', message.i18n)
    }

    // ── 1. Версионирование ────────────────────────────────────────────────────
    if (effectRegistry.has(pluginType as Parameters<typeof effectRegistry.has>[0])) {
      const { isNewerVersion } = await import('@shared/lib/effect-registry/semver')
      const currentVersion = effectRegistry.getVersion(
        pluginType as Parameters<typeof effectRegistry.getVersion>[0]
      )
      if (!isNewerVersion(incomingVersion, currentVersion)) {
        this.onStatus({
          type: 'plugin_rejected',
          pluginType,
          version: incomingVersion,
          reason: `downgrade or same version (current: v${currentVersion})`,
        })
        return
      }
    }

    // ── 2. Верификация подписи (Ed25519) ──────────────────────────────────────
    // Проверяется: keyId, sha256(moduleCode)===moduleCodeHash и подпись над
    // каноническим payload. Fail-closed без публичного ключа.
    //
    // Одноразовый auto-recovery на случай key-id-mismatch: если бэкенд
    // рестартнул (или ротация ключа), у overlay в памяти лежит
    // старый publicKey → verify упадёт с 'key-id-mismatch'. В этом случае
    // подтягиваем свежий ключ и повторяем verify. Если снова false —
    // это уже настоящая ошибка (bad-signature / hash-mismatch / битый ключ),
    // и мы плагин отвергаем.
    const verifyParams = {
      pluginType,
      version: incomingVersion,
      moduleCode,
      moduleCodeHash,
      signature,
      keyId,
    }
    let isValid = await pluginVerifier.verify(verifyParams)

    if (!isValid) {
      const failure = pluginVerifier.getLastFailure()
      // eslint-disable-next-line no-console
      console.warn(`[overlay] plugin verify failed for ${pluginType} v${incomingVersion}:`, failure)

      const shouldReloadKey = failure?.code === 'key-id-mismatch' || failure?.code === 'no-key'
      if (shouldReloadKey) {
        // eslint-disable-next-line no-console
        console.info(`[overlay] reloading public key and retrying verify for ${pluginType}`)
        const reloaded = await sessionService.reloadPublicKey()
        if (reloaded) {
          isValid = await pluginVerifier.verify(verifyParams)
          if (!isValid) {
            // eslint-disable-next-line no-console
            console.warn(
              `[overlay] verify still failing after key reload:`,
              pluginVerifier.getLastFailure()
            )
          }
        }
      }
    }

    if (!isValid) {
      const failure = pluginVerifier.getLastFailure()
      const reasonText = failure
        ? `invalid Ed25519 signature (${failure.code})`
        : 'invalid Ed25519 signature'
      this.onStatus({
        type: 'plugin_rejected',
        pluginType,
        version: incomingVersion,
        reason: reasonText,
      })
      return
    }

    // ── 3. Загрузка модуля (только blob из чистого ESM-текста) ─────────────────
    try {
      const importUrl = this.createBlobUrl(pluginType, moduleCode)

      const mod = (await import(/* @vite-ignore */ importUrl)) as Record<string, unknown>
      const pluginExport = (mod.default ?? mod['plugin']) as EffectPlugin<OverlayEvent> | undefined

      if (!pluginExport?.component || typeof pluginExport.component !== 'function') {
        this.revokeBlobUrl(pluginType)
        // eslint-disable-next-line no-console
        console.warn(`[overlay] plugin_rejected (bad export): ${pluginType} v${incomingVersion}`)
        this.onStatus({
          type: 'plugin_rejected',
          pluginType,
          version: incomingVersion,
          reason: 'module must export { type, component } as default',
        })
        return
      }

      pluginExport.version = incomingVersion

      if (pluginExport.sound?.src) {
        void audioService.preload(pluginExport.sound.src)
      }

      // ── 5. Регистрация ────────────────────────────────────────────────────
      effectRegistry.register(pluginExport)

      // eslint-disable-next-line no-console
      console.info(
        `[overlay] plugin_ready ${pluginType} v${incomingVersion} (verify transport: ${pluginVerifier.getTransport()})`,
      )

      // Уведомляем бэкенд — плагин готов, можно слать события
      this.onStatus({
        type: 'plugin_ready',
        pluginType,
        version: incomingVersion,
      })
    } catch (err) {
      this.revokeBlobUrl(pluginType)
      // eslint-disable-next-line no-console
      console.warn(`[overlay] plugin_rejected (load error): ${pluginType} v${incomingVersion}`, err)
      this.onStatus({
        type: 'plugin_rejected',
        pluginType,
        version: incomingVersion,
        reason: String(err),
      })
    }
  }

  /**
   * Обновляет словарь переводов плагина (сообщение update_i18n при смене языка
   * стримера). Меняет ТОЛЬКО i18n-store → активные эффекты ре-рендерятся с новым
   * t() через подписку zustand. Плагин НЕ пересоздаётся и НЕ переверифицируется.
   */
  updateI18n(pluginType: string, locale: string, dict: Record<string, string>): void {
    usePluginI18nStore.getState().setDict(pluginType, locale, dict)
  }

  updateSound(pluginType: string, sound: RemoteSoundConfig | null): void {
    const plugin = effectRegistry.get(pluginType as Parameters<typeof effectRegistry.get>[0])
    if (!plugin) return
    plugin.sound = sound ?? undefined
    if (sound?.src) void audioService.preload(sound.src)
  }

  private createBlobUrl(pluginType: string, code: string): string {
    this.revokeBlobUrl(pluginType)
    // moduleCode — всегда чистый ESM-текст (base64 больше не поддерживается).
    const blob = new Blob([code], { type: 'text/javascript' })
    const url = URL.createObjectURL(blob)
    this.blobUrls.set(pluginType, url)
    return url
  }

  private revokeBlobUrl(pluginType: string): void {
    const existing = this.blobUrls.get(pluginType)
    if (existing) {
      URL.revokeObjectURL(existing)
      this.blobUrls.delete(pluginType)
    }
  }

  dispose(): void {
    this.blobUrls.forEach((url) => URL.revokeObjectURL(url))
    this.blobUrls.clear()
  }
}

export const dynamicPluginLoader = new DynamicPluginLoader()
